import { useEffect, useRef, useState } from 'react';
import styles from './Home.module.scss';
import Button2 from '../../components/Buttons/Button2/Button2';
import { Artwork } from '../../ressources/interfaces/artwork';
import ArtworkComponent from '../../components/Artwork/Artwork';
import { get } from '../../fetchers/fetchers';
import { ARTWORK_CONTROLLER_URL, PRINT_FORMAT_URL } from '../../ressources/constants/apiRoutes';
import ArtworkLargeView from '../../components/Modals/ArtworkLargeView/ArtworkLargeView';
import QuoteRequest from '../../components/Modals/QuoteRequest/QuoteRequest';
import PrintableArtwork from '../../components/Modals/PrintableArtwork/PrintableArtwork';
import { MOBILE_BREAKPOINT } from '../../assets/constants';
import { PrintFormat } from '../../ressources/interfaces/printFormat';

const SERIES_ONE_TYPE = "SeriesOne";
const SERIES_TWO_TYPE = "SeriesTwo";
const SERIES_THREE_TYPE = "SeriesThree";
const ARTWORK_SIZE = 300;
const BUTTON_ONE_TEXT = "Generate";
const BUTTON_TWO_TEXT = "Buy";
// Checkout (shipping and card steps) stays hidden until it works; set REACT_APP_CHECKOUT_ENABLED=true to work on it locally
const CHECKOUT_ENABLED = process.env.REACT_APP_CHECKOUT_ENABLED === 'true';

const Home = () => {
  const [SeriesOne, setSeriesOne] = useState({imageURL:"", id:0, type:SERIES_ONE_TYPE});
  const [SeriesTwo, setSeriesTwo] = useState({imageURL:"", id:0, type:SERIES_TWO_TYPE});
  const [SeriesThree, setSeriesThree] = useState({imageURL:"", id:0, type:SERIES_THREE_TYPE});
  const [loadingStates, setLoadingStates] = useState({[SERIES_ONE_TYPE]: false, [SERIES_TWO_TYPE]: false, [SERIES_THREE_TYPE]: false});
  // const [gallerySeriesId, setGallerySeriesId ] = useState<string | null>(null);
  const [largeImageURL, setLargeImageURL ] = useState<string | null>(null);
  const [largeArtwork, setLargeArtwork ] = useState<Artwork | null>(null);
  const [quoteImageURL, setQuoteImageURL ] = useState<string | null>(null);
  const [displayQuoteRequestModal, setDisplayQuoteRequestModal ] = useState<boolean>(false);
  const [printable, setPrintable ] = useState<{display: boolean, isLoading: boolean, title: string | null, artworkURL: string | null, format: PrintFormat | null, error: string | null}>({display: false, isLoading: false, title: null, artworkURL: null, format: null, error: null});
  const printableRequestId = useRef(0);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [descriptionExpanded, setDescriptionExpanded] = useState<{[key: string]: boolean}>({
    [SERIES_ONE_TYPE]: false,
    [SERIES_TWO_TYPE]: false,
    [SERIES_THREE_TYPE]: false
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      }, 150);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    generateArtwork(SERIES_ONE_TYPE);
    generateArtwork(SERIES_TWO_TYPE);
    generateArtwork(SERIES_THREE_TYPE);
  }, [])

  const toggleDescription = (seriesType: string) => {
    setDescriptionExpanded(prev => ({
      ...prev,
      [seriesType]: !prev[seriesType]
    }));
  };

  const receiveLargeImageURL = (imageURL: string | null) => {
    setLargeImageURL(imageURL);
  };

  const hideQuoteRequestModal = () => {
    setDisplayQuoteRequestModal(false);
  }

  // The preview is drawn in the browser from the server's print format; the print-ready
  // file is only built by the PrintableArtwork lambda once the artwork is bought
  const loadPrintPreview = async (artworkURL: string, title: string) => {
    if (!artworkURL) return;
    // Only the latest request may update the modal, in case Buy is pressed again while one is in flight
    const requestId = ++printableRequestId.current;
    setPrintable({display: true, isLoading: true, title: title, artworkURL: artworkURL, format: null, error: null});
    try {
      const format = await get<PrintFormat>(PRINT_FORMAT_URL, {artwork_url: artworkURL});
      if (requestId === printableRequestId.current) setPrintable(prev => ({...prev, isLoading: false, format: format}));
    } catch (error: any) {
      console.error('Failed to load the print format:', error);
      const data = error?.response?.data;
      const message = data?.error ?? (error?.response ? `Failed to load the print preview (${error.response.status}: ${data?.message ?? error.response.statusText})` : `Failed to load the print preview (${error?.message})`);
      if (requestId === printableRequestId.current) setPrintable(prev => ({...prev, isLoading: false, error: message}));
    }
  };

  // The quote request popup is disabled for now; buying goes straight to the printable preview
  const buyArtwork = (artwork: Artwork) => {
    // "SeriesThree" -> "Series Three #123", matching the number shown under the artwork
    const title = `${artwork.type.replace(/([a-z])([A-Z])/g, '$1 $2')} #${artwork.id}`;
    loadPrintPreview(artwork.imageURL, title);
  };

  const generateArtwork = async (seriesType: string) => {
    setLoadingStates(prev => ({...prev, [seriesType]: true}));
    try {
      const response = await get<{success: boolean, imageUrl: string, key: string, artworkId: number}>(ARTWORK_CONTROLLER_URL + '/generateArtwork/' + seriesType);
      if (seriesType === SERIES_ONE_TYPE) setSeriesOne(prev => ({...prev, imageURL: response.imageUrl, id: response.artworkId}));
      if (seriesType === SERIES_TWO_TYPE) setSeriesTwo(prev => ({...prev, imageURL: response.imageUrl, id: response.artworkId}));
      if (seriesType === SERIES_THREE_TYPE) setSeriesThree(prev => ({...prev, imageURL: response.imageUrl, id: response.artworkId}));
    } finally {
      setLoadingStates(prev => ({...prev, [seriesType]: false}));
    }
  };

  return (
    <div className={styles.Home} data-testid="Home">
      {/* <GalleryViewer seriesId={gallerySeriesId} sendGallerySeriesId={receiveGallerySeriesId}></GalleryViewer> */}
      <ArtworkLargeView imageURL={largeImageURL} sendImageURL={receiveLargeImageURL} action={() => {if (largeArtwork) buyArtwork(largeArtwork)}}></ArtworkLargeView>
      <QuoteRequest imageURL={quoteImageURL} displayModal={displayQuoteRequestModal} hideModal={() => hideQuoteRequestModal()}></QuoteRequest>
      <PrintableArtwork displayModal={printable.display} hideModal={() => setPrintable(prev => ({...prev, display: false}))} title={printable.title} artworkURL={printable.artworkURL} format={printable.format} isLoading={printable.isLoading} error={printable.error} checkoutEnabled={CHECKOUT_ENABLED}></PrintableArtwork>
      <div className={styles.ArtworkContainer}>
        <ArtworkComponent size={ARTWORK_SIZE} imageURL={SeriesOne.imageURL} id={SeriesOne.id} type={SeriesOne.type} isLoading={loadingStates[SERIES_ONE_TYPE]} onClick={isMobile ? undefined : () => {setLargeImageURL(SeriesOne.imageURL); setLargeArtwork(SeriesOne); setQuoteImageURL(SeriesOne.imageURL)}}/>
        <div className={styles.Description}>
          <p className={isMobile && !descriptionExpanded[SERIES_ONE_TYPE] ? styles.truncated : ''}>SeriesOne grew out of an architecture project after I completed my software engineering degree. We were asked to explore shapes by drawing silhouettes of how a building might occupy a square plot using grids and multiple iterations. To push beyond my own preconceptions, I automated the process, adding randomness through a simple algorithm to generate and experiment with forms. From the many variations, I selected the most compelling shapes and developed them in 3D, imagining them as livable spaces. This project laid the foundation for my generative art practice, using code, randomness, and iteration as tools for design and discovery.</p>
          {isMobile && (
            <button className={styles.ToggleButton} onClick={() => toggleDescription(SERIES_ONE_TYPE)}>
              {descriptionExpanded[SERIES_ONE_TYPE] ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className={styles.Footer}>
            <Button2 action={() => generateArtwork(SERIES_ONE_TYPE)}>{BUTTON_ONE_TEXT}</Button2>
            <Button2 action={() => buyArtwork(SeriesOne)}>{BUTTON_TWO_TEXT}</Button2>
          </div>
        </div>
      </div>
      <div className={styles.ArtworkContainer}>
        <ArtworkComponent size={ARTWORK_SIZE} imageURL={SeriesTwo.imageURL} id={SeriesTwo.id} type={SeriesTwo.type} isLoading={loadingStates[SERIES_TWO_TYPE]} onClick={isMobile ? undefined : () => {setLargeImageURL(SeriesTwo.imageURL); setLargeArtwork(SeriesTwo); setQuoteImageURL(SeriesTwo.imageURL)}}/>
        <div className={styles.Description}>
          <p className={isMobile && !descriptionExpanded[SERIES_TWO_TYPE] ? styles.truncated : ''}>SeriesTwo explores three-dimensional form emerging from a single continuous line. Each piece is generated by an algorithm guiding one unbroken line on a random path within an invisible circle. A gravity parameter pulls the line toward a random point within the boundary, creating density gradients that transform the flat constraint into the illusion of spherical depth. Through thousands of movements, the line builds textured surfaces evoking celestial bodies—often compared to moons. Simple rules and patient accumulation generate forms that feel organic, planetary, and unexpectedly dimensional.</p>
          {isMobile && (
            <button className={styles.ToggleButton} onClick={() => toggleDescription(SERIES_TWO_TYPE)}>
              {descriptionExpanded[SERIES_TWO_TYPE] ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className={styles.Footer}>
            <Button2 action={() => generateArtwork(SERIES_TWO_TYPE)}>{BUTTON_ONE_TEXT}</Button2>
            <Button2 action={() => buyArtwork(SeriesTwo)}>{BUTTON_TWO_TEXT}</Button2>
          </div>
        </div>
      </div>
      <div className={styles.ArtworkContainer}>
        <ArtworkComponent size={ARTWORK_SIZE} imageURL={SeriesThree.imageURL} id={SeriesThree.id} type={SeriesThree.type} isLoading={loadingStates[SERIES_THREE_TYPE]} onClick={isMobile ? undefined : () => {setLargeImageURL(SeriesThree.imageURL); setLargeArtwork(SeriesThree); setQuoteImageURL(SeriesThree.imageURL)}}/>
        <div className={styles.Description}>
          <p className={isMobile && !descriptionExpanded[SERIES_THREE_TYPE] ? styles.truncated : ''}>SeriesThree uses custom algorithms to blend randomness, geometry, and motion, creating visual rhythm and structure. Dense point clouds of hundreds of thousands of calculated coordinates form translucent waves that shift between precision and fluidity. From afar, these clouds read as continuous rhythmic fields; up close, the carefully arranged dots reveal their underlying architecture. Color becomes a generative element too—randomly paired foreground and background tones add unpredictability, reshaping depth, contrast, and motion. Through the interplay of density, shape, rhythm, and color, what first appears as chaos resolves into dynamic, coherent structures, revealing the hidden order that emerges from randomness.</p>
          {isMobile && (
            <button className={styles.ToggleButton} onClick={() => toggleDescription(SERIES_THREE_TYPE)}>
              {descriptionExpanded[SERIES_THREE_TYPE] ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className={styles.Footer}>
            <Button2 action={() => generateArtwork(SERIES_THREE_TYPE)}>{BUTTON_ONE_TEXT}</Button2>
            <Button2 action={() => buyArtwork(SeriesThree)}>{BUTTON_TWO_TEXT}</Button2>
          </div>
        </div>
      </div>
    </div>
  )
};

export default Home;
